import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Mic, Square, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useVoiceInput } from "@/lib/useVoiceInput";

export default function ConditionNotesField({ draft, patch }) {
  const [tidying, setTidying] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const value = draft.condition_notes || "";

  const voice = useVoiceInput(async (spoken) => {
    setTidying(true);
    try {
      const { data } = await api.post("/condition-notes/tidy", { text: spoken, title: draft.title || "" });
      if (data.notes) setSuggestion(data.notes);
      else toast.info("Didn't hear any flaws — try describing the condition");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't tidy that");
    } finally {
      setTidying(false);
    }
  });

  const accept = () => {
    patch({ condition_notes: (value.trim() ? `${value.trim()} ${suggestion}` : suggestion).slice(0, 1000) });
    setSuggestion("");
  };

  const busy = tidying || voice.state === "transcribing";
  return (
    <div data-testid="field-condition-notes">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-600">Condition notes (flaws)</span>
        <button type="button" onClick={voice.state === "recording" ? voice.stop : voice.start} disabled={busy}
          data-testid="condition-notes-mic" aria-label={voice.state === "recording" ? "Stop recording" : "Describe flaws by voice"}
          className={`inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-[11px] font-semibold transition-colors disabled:opacity-60 ${voice.state === "recording" ? "animate-pulse border-rose-500 bg-rose-500 text-white" : "border-slate-200 bg-white text-[#0053DA] hover:border-[#0053DA]"}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : voice.state === "recording" ? <Square className="h-3 w-3 fill-current" /> : <Mic className="h-3 w-3" />}
          {voice.state === "recording" ? `Stop · ${voice.seconds}s` : busy ? "Tidying…" : "Speak flaws"}
        </button>
      </div>
      {suggestion && (
        <div className="mb-2 rounded-md border border-[#0053DA]/30 bg-blue-50/60 p-2" data-testid="condition-notes-suggestion">
          <p className="break-words text-xs text-slate-800">{suggestion}</p>
          <div className="mt-1.5 flex gap-2">
            <button type="button" onClick={accept} data-testid="condition-notes-accept"
              className="inline-flex h-7 items-center gap-1 rounded-full bg-[#0053DA] px-3 text-[11px] font-semibold text-white"><Check className="h-3 w-3" /> Add</button>
            <button type="button" onClick={() => setSuggestion("")} data-testid="condition-notes-discard"
              className="inline-flex h-7 items-center gap-1 px-2 text-[11px] text-slate-500"><X className="h-3 w-3" /> Discard</button>
          </div>
        </div>
      )}
      <Textarea value={value} onChange={(e) => patch({ condition_notes: e.target.value.slice(0, 1000) })} rows={2}
        placeholder="e.g. Small scuff on the back corner, works perfectly" className="text-sm" data-testid="input-condition-notes" />
      <p className="mt-1 text-[10px] text-slate-400">Shown to buyers as eBay's condition description (used items only).</p>
    </div>
  );
}
